import React, { useEffect, useState } from 'react';
import { DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { listExpedientDocuments } from '../services/s3Service';
import { formatFileSize } from '../utils/formatters';
import { DOCUMENT_CATEGORY_LABELS } from '@claimup/shared/constants/expedient-documents';

/**
 * Elegir, de los documentos que YA tiene el expediente, cuáles adjuntar.
 *
 * Hasta ahora el clip del compositor solo abría el explorador del ordenador, así
 * que mandarle al deudor la factura que ya estaba en el expediente obligaba a
 * bajarla y subirla otra vez: el mismo PDF dos veces en S3, y cuatro pasos donde
 * debería haber uno. En AR es el caso normal, porque el documento suele entrar
 * por el volcado de PDF.
 *
 * ── Salen TODOS, y los que no se mandan van marcados ────────────────────────
 * Decisión de Edgar (19/10/2026): en el camino manual **no hay veto**, el gestor
 * manda lo que quiera. Pero en esta lista pueden aparecer notas internas del
 * cliente o informes, y sin ninguna marca mandarle al deudor el papel
 * equivocado es un clic. Así que la marca no impide nada: obliga a verlo.
 *
 * `document_category` y `shareable_with_debtor` son **solo de AR**. En un
 * expediente de recobro vienen vacíos y la lista sale sin marcas: no es un
 * hueco, es que ahí no hay nada que marcar.
 */
const SelectorDocumentosExpediente = ({ expedientId, yaAdjuntos = [], onElegir, onCerrar }) => {
  const [documentos, setDocumentos] = useState(null);
  const [error, setError] = useState('');
  const [marcados, setMarcados] = useState([]);

  useEffect(() => {
    let vivo = true;
    listExpedientDocuments(expedientId)
      .then(docs => {
        if (vivo) setDocumentos(docs);
      })
      .catch(() => {
        if (vivo) setError('No se han podido cargar los documentos del expediente.');
      });
    return () => {
      vivo = false;
    };
  }, [expedientId]);

  // Los que ya están en el correo no se vuelven a ofrecer: adjuntar dos veces el
  // mismo fichero no da error en ningún sitio y el deudor recibe el duplicado.
  const puestos = new Set(yaAdjuntos.map(a => a.s3_key).filter(Boolean));
  const disponibles = (documentos || []).filter(d => !puestos.has(d.s3_key));

  const alternar = s3Key =>
    setMarcados(prev => (prev.includes(s3Key) ? prev.filter(k => k !== s3Key) : [...prev, s3Key]));

  const confirmar = () => {
    onElegir(
      disponibles
        .filter(d => marcados.includes(d.s3_key))
        .map(d => ({
          // La MISMA forma que lo subido del ordenador, para que nada por debajo
          // tenga que distinguir de dónde vino.
          filename: d.file_name,
          mime_type: d.file_type,
          size: d.size ?? 0,
          s3_key: d.s3_key,
        }))
    );
    onCerrar();
  };

  const fecha = iso => (iso ? new Date(iso).toLocaleDateString('es-ES') : null);

  return (
    <div className='border border-slate-200 rounded-lg bg-white p-3 mt-2'>
      <div className='flex items-start justify-between gap-2 mb-2'>
        <p className='text-xs font-semibold text-slate-600'>Documentos del expediente</p>
        <button
          type='button'
          onClick={onCerrar}
          className='text-xs text-slate-500 hover:text-slate-800'
        >
          Cerrar
        </button>
      </div>

      {error && <p className='text-xs text-red-600'>{error}</p>}

      {!error && documentos === null && <p className='text-xs text-slate-500'>Cargando…</p>}

      {!error && documentos !== null && disponibles.length === 0 && (
        <p className='text-xs text-slate-500'>
          {documentos.length === 0
            ? 'Este expediente no tiene documentos subidos.'
            : 'Ya están todos adjuntados a este correo.'}
        </p>
      )}

      {disponibles.length > 0 && (
        <>
          <ul className='space-y-1 max-h-56 overflow-y-auto'>
            {disponibles.map(d => (
              <li key={d.s3_key}>
                <label className='flex items-start gap-2 text-xs px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={marcados.includes(d.s3_key)}
                    onChange={() => alternar(d.s3_key)}
                    className='mt-0.5'
                  />
                  <DocumentTextIcon className='w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5' />
                  <span className='min-w-0 flex-1'>
                    <span className='block text-slate-800 truncate'>{d.file_name}</span>
                    <span className='block text-[11px] text-slate-400'>
                      {[
                        d.document_category ? DOCUMENT_CATEGORY_LABELS[d.document_category] : null,
                        fecha(d.upload_at),
                        d.size ? formatFileSize(d.size) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {/* Solo en AR: en recobro el campo no se usa y no se afirma nada. */}
                    {d.document_category !== null && !d.shareable_with_debtor && (
                      <span className='flex items-center gap-1 text-[11px] text-red-600 mt-0.5'>
                        <ExclamationTriangleIcon className='w-3 h-3 flex-shrink-0' />
                        No está marcado como enviable al deudor
                      </span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          <div className='flex items-center gap-2 mt-2'>
            <button
              type='button'
              onClick={confirmar}
              disabled={marcados.length === 0}
              className='px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed'
            >
              {marcados.length === 0
                ? 'Adjuntar'
                : `Adjuntar ${marcados.length} ${marcados.length === 1 ? 'documento' : 'documentos'}`}
            </button>
            {marcados.some(k => {
              const d = disponibles.find(x => x.s3_key === k);
              return d && d.document_category !== null && !d.shareable_with_debtor;
            }) && (
              <span className='text-[11px] text-red-600'>
                Vas a mandar algo no marcado como enviable.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SelectorDocumentosExpediente;
